// FormTemplateService - Manages form templates and their definitions (Firestore-backed)
import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';

export interface FormFieldElement {
  id: string;
  type: 'short-response' | 'long-response' | 'single-option' | 'multiple-options' | 'dropdown' | 'date' | 'signature' | 'group' | 'heading' | 'description' | 'divider';
  content: {
    question?: string;
    description?: string;
    options?: string[];
    required?: boolean;
    placeholder?: string;
    title?: string;
    text?: string;
    textResponses?: boolean[];
  };
}

export interface FormTemplateDefinition {
  id: string;
  name: string;
  category: 'intake' | 'treatment-plan' | 'assessment' | 'consent' | 'hr' | 'measures';
  description?: string;
  required: boolean;
  elements: FormFieldElement[];
  createdAt: string;
  updatedAt: string;
  version: number;
}

const COLLECTION = 'form_templates';

class FormTemplateService {
  private static instance: FormTemplateService;
  private listeners: (() => void)[] = [];
  private unsubFirestore: Unsubscribe | null = null;
  private cache: FormTemplateDefinition[] | null = null;

  constructor() {
    this.startRealtimeListener();
  }

  public static getInstance(): FormTemplateService {
    if (!FormTemplateService.instance) {
      FormTemplateService.instance = new FormTemplateService();
    }
    return FormTemplateService.instance;
  }

  private startRealtimeListener(): void {
    try {
      const colRef = collection(db, COLLECTION);
      this.unsubFirestore = onSnapshot(colRef, (snapshot) => {
        this.cache = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as FormTemplateDefinition));
        this.notifyListeners();
      }, (error) => {
        console.warn('FormTemplateService: Firestore listener error:', error);
      });
    } catch (error) {
      console.warn('FormTemplateService: Could not start listener:', error);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  public subscribe(listener: () => void): void {
    this.listeners.push(listener);
  }

  public unsubscribe(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  // Template Management Methods
  public async saveFormTemplate(
    templateName: string,
    elements: FormFieldElement[],
    category: FormTemplateDefinition['category'] = 'intake',
    required: boolean = false,
    description?: string
  ): Promise<FormTemplateDefinition> {
    const templates = await this.fetchAll();
    const existingTemplate = templates.find(t => t.name === templateName);

    const now = new Date().toISOString();

    if (existingTemplate) {
      // Update existing template
      const updated: FormTemplateDefinition = {
        ...existingTemplate,
        elements,
        updatedAt: now,
        version: existingTemplate.version + 1,
        category,
        required,
        description
      };

      await setDoc(doc(db, COLLECTION, updated.id), updated);
      return updated;
    } else {
      // Create new template
      const newId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newTemplate: FormTemplateDefinition = {
        id: newId,
        name: templateName,
        category,
        description,
        required,
        elements,
        createdAt: now,
        updatedAt: now,
        version: 1
      };

      await setDoc(doc(db, COLLECTION, newId), newTemplate);
      return newTemplate;
    }
  }

  public async getFormTemplate(templateName: string): Promise<FormTemplateDefinition | undefined> {
    const templates = await this.fetchAll();
    return templates.find(t => t.name === templateName);
  }

  public async getAllFormTemplates(): Promise<FormTemplateDefinition[]> {
    return [...(await this.fetchAll())];
  }

  public async getTemplatesByCategory(category: FormTemplateDefinition['category']): Promise<FormTemplateDefinition[]> {
    const templates = await this.fetchAll();
    return templates.filter(t => t.category === category);
  }

  public async deleteFormTemplate(templateName: string): Promise<boolean> {
    const templates = await this.fetchAll();
    const toDelete = templates.find(t => t.name === templateName);

    if (toDelete) {
      await deleteDoc(doc(db, COLLECTION, toDelete.id));
      return true;
    }

    return false;
  }

  public async duplicateFormTemplate(templateName: string, newName: string): Promise<FormTemplateDefinition | null> {
    const originalTemplate = await this.getFormTemplate(templateName);
    if (!originalTemplate) return null;

    const now = new Date().toISOString();
    const newId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const duplicatedTemplate: FormTemplateDefinition = {
      ...originalTemplate,
      id: newId,
      name: newName,
      createdAt: now,
      updatedAt: now,
      version: 1
    };

    await setDoc(doc(db, COLLECTION, newId), duplicatedTemplate);
    return duplicatedTemplate;
  }

  // Integration with FormAssignmentService
  public async getAssignableTemplates(): Promise<Array<{
    id: string;
    name: string;
    category: FormTemplateDefinition['category'];
    required: boolean;
    description?: string;
  }>> {
    const templates = await this.getAllFormTemplates();
    return templates.map(template => ({
      id: template.id,
      name: template.name,
      category: template.category,
      required: template.required,
      description: template.description
    }));
  }

  // Client-side form rendering data
  public async getFormForClient(templateName: string): Promise<FormTemplateDefinition | null> {
    const template = await this.getFormTemplate(templateName);
    if (!template) return null;

    // Return a clean copy for client rendering
    return {
      ...template,
      elements: template.elements.map(element => ({
        ...element,
        content: {
          ...element.content
        }
      }))
    };
  }

  /** Fetch all templates, preferring cache when available */
  private async fetchAll(): Promise<FormTemplateDefinition[]> {
    if (this.cache !== null) {
      return this.cache;
    }
    try {
      const snapshot = await getDocs(collection(db, COLLECTION));
      this.cache = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as FormTemplateDefinition));
      return this.cache;
    } catch (error) {
      console.error('FormTemplateService: Error fetching templates:', error);
      return [];
    }
  }
}

export default FormTemplateService;
